import React from 'react'
import { MyAvatar } from '@interstellar-component'

// Shared screening-lifecycle timeline. Backend item shape
// (workforce.repositories.js#getWorkforceActivity):
//   { id, event_code, title, text, actor_name, actor: {avatar_full_url}|null,
//     is_system, note, created_at }
// Used by the workforce Personal Information "Activity" card and the
// report-enquiry create-employee detail slider so both render identically.

function relativeTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const sec = Math.floor(diffMs / 1000)
  if (sec < 60) return 'Just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function mapApiActivity(item) {
  return {
    id: item.id,
    title: item.title ?? 'Activity',
    subtext: relativeTime(item.created_at),
    supportingText: item.text ?? '',
    actorName: item.actor_name ?? null,
    avatarUrl: item.is_system ? null : item.actor?.avatar_full_url ?? item.actor?.avatar_url ?? null,
    note: item.note ?? null,
  }
}

// Render the supporting line with the actor's name in bold (backend embeds the
// name inline; split on it so only the name is emphasised).
function renderSupporting(text, name) {
  if (!name || !text.includes(name)) return text
  const parts = text.split(name)
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && (
        <span className="font-semibold text-gray-900">{name}</span>
      )}
    </React.Fragment>
  ))
}

export default function ActivityTimeline({ items = [], emptyText = 'No activity recorded yet.' }) {
  const activities = (items || []).map(mapApiActivity)

  if (activities.length === 0) {
    return <p className="py-3 text-sm text-gray-400">{emptyText}</p>
  }

  return (
    <div className="flex flex-col gap-6">
      {activities.map((activity, index) => (
        <div key={activity.id ?? index} className="flex gap-3 relative last:mb-0">
          {/* Timeline connector */}
          {index !== activities.length - 1 && (
            <div className="absolute left-[16px] top-[32px] bottom-[-24px] w-[1px] bg-gray-100" />
          )}

          {/* Avatar — MyAvatar handles image load + icon fallback; system
              events have no actor, so it falls back to the generic icon. */}
          <div className="flex-shrink-0 z-10">
            <MyAvatar photo={activity.avatarUrl} size={32} iconSize={16} />
          </div>

          {/* Content */}
          <div className="flex flex-col gap-1 w-full">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-gray-900">{activity.title}</span>
              <span className="text-xs text-gray-400 whitespace-nowrap">{activity.subtext}</span>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed font-medium">
              {renderSupporting(activity.supportingText, activity.actorName)}
            </p>

            {activity.note && (
              <div className="mt-2 p-3 rounded-lg border border-gray-100 bg-white">
                <p className="text-sm text-gray-500 italic">&quot;{activity.note}&quot;</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
